import { Component, effect, inject, ChangeDetectionStrategy } from '@angular/core';
import { Register, TreeComponent } from '@xanadu/core';
import { Content, Folder, Site, Genus, Identity, Secure } from '@xanadu/model';
import { AuthSignal, BatchSignal } from '@xanadu/signal';
import { NodeModel, Node, Branch } from '@xanadu/model';
import { Observable } from 'rxjs';
import { ContentMenuBuilder } from '@xanadu/menu';

@Component({
    selector: 'app-content-tree',
    templateUrl: './content-tree.component.html',
    styleUrls: ['./content-tree.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
@Register('content-tree')
export class ContentTreeComponent extends TreeComponent<Content | Folder | Site> {
    readonly menuBuilder = inject(ContentMenuBuilder);
    private readonly batchSignal = inject(BatchSignal);

    identity?: Identity;
    locateIds?: string[];

    loadBatch: boolean = true;

    protected override get nodeType(): string | undefined {
        return 'content';
    }

    constructor(
    ) {
        super();

        effect(() => {
            this.identity = this.authSignal.identity();
        });

        effect(() => {
            const model = this.treeSignal.import();

            if (model && this.current?.node instanceof Genus) {
                const genus = this.current.node as Genus;
                const { name, ...data } = model;
                const content = new Content({ name, data, prototypeId: genus.prototypeId, siteId: genus.siteId });
                content.prototype = genus.prototype;

                this.insertChild(content);
            }
        });

        effect(() => {
            const models = this.treeSignal.importMany();

            if (models?.length && this.current?.node instanceof Genus) {
                const genus = this.current.node as Genus;
                models.forEach(model => {
                    const { name, ...data } = model;
                    const content = new Content({ name, data, prototypeId: genus.prototypeId, prototype: genus.prototype });

                    this.insertChild(content, true);
                });
            }
        });

        effect(() => {
            const result = this.batchSignal.done();

            result?.contents?.forEach(content => {
                const entry = this.registry.get(content._id);
                if (entry?.model.info.content) {
                    entry.model.info.content.copy(content);
                }
            });

            this.batchSignal.update.set(undefined);
        });

        effect(() => {
            const root = this.rootModel();
            if (root) {
                this.treeSignal.contentRoot.set(root);
            }
        });
    }

    onSite() {
        const site = new Site(undefined);
        site.name = 'new site';
        this.insertChild(site);


        if (this.current?.node instanceof Genus) {
            const genus = this.current.node as Genus;

            site.prototypeId = genus.prototypeId;
            site.prototype = genus.prototype;
            site.accessLevel = genus.accessLevel;
            site.canDelete = true;
        }
    }

    onGenus() {
        const genus = new Genus(undefined);
        genus.name = 'new genus';
        this.insertChild(genus);

        if (this.current?.node instanceof Secure) {
            const secure = this.current.node as Secure;
            if (secure instanceof Site) {
                if (secure._id) {
                    genus.siteId = secure._id;
                }
            } else if (secure instanceof Folder) {
                genus.siteId = secure.siteId;
            }
            genus.accessLevel = secure.accessLevel;
            genus.canDelete = true;
        }
    }

    onFolder() {
        const folder = new Folder(undefined);
        folder.name = 'new folder';
        this.insertChild(folder);

        if (this.current?.node instanceof Secure) {
            const secure = this.current.node as Secure;
            if (secure instanceof Site) {
                const site = secure as Site;
                if (site._id) {
                    folder.siteId = site._id;
                }
            } else if (secure instanceof Genus) {
                const genus = secure as Genus;
                if (genus.prototypeId) {
                    folder.prototypeId = genus.prototypeId;
                    folder.prototype = genus.prototype;
                    if (genus instanceof Folder) {
                        folder.inheritAspects = (genus.aspects ?? []).concat(genus?.inheritAspects ?? []);
                    } else {
                        folder.inheritAspects = genus.aspects;
                    }
                }
                folder.siteId = genus.siteId;
            }
            folder.accessLevel = secure.accessLevel;
            folder.canDelete = true;
        }
    }

    onContent() {
        const content = new Content(undefined);
        content.name = 'new content';
        content.data = {};

        this.insertChild(content);

        if (this.current?.node instanceof Genus) {
            content.prototypeId = this.current.node.prototypeId;
            content.prototype = this.current.node.prototype;
            content.siteId = this.current.node.siteId;
        }
    }

    onPaste() {
        this.paste();
    }

    protected insertChild(node: Node, silent: boolean = false): void {
        const insert = node instanceof Folder ? (parent: NodeModel, child: NodeModel) => {
            if (parent.descents) {
                const last = parent.descents.findIndex(m => m.node instanceof Genus);
                if (last > -1) {
                    parent.descents.splice(last, 0, child);
                } else {
                    parent.descents.push(child);
                }
            }
        } : undefined;

        this.addChild(node, silent, insert);
    }

    protected replace(node: Node, branch: Branch): Observable<Content | Folder> {
        return this.dataService.replace<Content | Folder>(node as any, branch);
    }

    protected dropOk(dragModel: NodeModel, dropModel: NodeModel): boolean {
        const dragNode = dragModel.node;
        const dropNode = dropModel.node as Branch;

        return dragNode.fit(dropNode);
    }
}
